import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';

import { SearchHelperService } from '@offconon/shared/utils/services/search';

import { setPagination, setSelectedFilters } from './data-access/store/search-box.actions';
import { getSearchBoxSelectedFilters } from './data-access/store/search-box.selectors';
import { SearchBoxFeatureModule } from './search-box-feature.module';
import { Searchbox } from './types/searchBox';

@Component({
  selector: 'offconon-shared-ui-search-box',
  imports: [
    ButtonModule,
    TranslateModule,
    SearchBoxFeatureModule,
    DividerModule,
    DialogModule,
    MatIcon,
    TooltipModule,
    FormsModule,
    InputTextModule,
  ],
  templateUrl: './shared-ui-search-box.component.html',
  styles: [
    `
      :host {
        display: block;
      }

      input:focus {
        outline: none;
        box-shadow: none;
      }
    `,
  ],
})
export class SharedUiSearchBoxComponent implements OnInit {
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private store$ = inject(Store);

  @Input() parameters: any;
  @Output() searchEvent = new EventEmitter<any>();

  private destroyRef = inject(DestroyRef);
  private searchService = inject(SearchHelperService);

  selectedFilters: Searchbox[] = [];

  globalSearchSideBar = false;

  getClosePanel() {
    this.globalSearchSideBar = false;
  }

  pagination: {
    page: number;
    page_size: number;
  } = {
    page: 1,
    page_size: 10,
  };

  ngOnInit(): void {
    this.searchService.searchPanel$.subscribe({
      next: (state: any) => {
        if (state) {
          this.searchOpen();
        }
      },
    });

    this.store$
      .select(getSearchBoxSelectedFilters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (filters) => {
          if (filters) {
            this.selectedFilters = filters;
            this.setUrlParams();
          }
        },
      });

    this.searchService.openFilterSearch$.subscribe({
      next: (state: any) => {
        if (state) {
          this.globalSearchSideBar = state;
        } else {
          this.globalSearchSideBar = false;
        }
      },
    });
  }

  setUrlParams() {
    const params: any = {};
    if (this.pagination) {
      params['page'] = this.pagination.page;
      params['page_size'] = this.pagination.page_size;
    }
    this.selectedFilters.forEach((filter) => {
      params[filter.urlParam] = this.selectedFilters
        .filter((i) => i.urlParam === filter.urlParam)
        .map((i) => i.value);
    });

    this._router
      .createUrlTree([], {
        relativeTo: this._route,
        queryParams: params,
        queryParamsHandling: '',
      })
      .toString();
  }

  submit() {
    this.searchService.setOnSearch();
    this.globalSearchSideBar = false;
    this.searchEvent.emit();
  }

  searchOpen() {
    this.globalSearchSideBar = true;
  }

  setFilterByQueryParams(params: any) {
    this.selectedFilters = [];
    if (params)
      for (const key in params) {
        if (params.hasOwnProperty(key)) {
          if (key === 'page' || key === 'page_size') {
            this.pagination[key] = params[key];
          } else {
            if (Array.isArray(params[key])) {
              params[key].forEach((element: string) => {
                this.selectedFilters.push({
                  value: element,
                  urlParam: key,
                });
              });
            } else {
              this.selectedFilters.push({
                value: params[key],
                urlParam: key,
              });
            }
          }
        }
      }
    this.store$.dispatch(setSelectedFilters({ selectedFilters: this.selectedFilters }));
    this.store$.dispatch(setPagination({ pagination: this.pagination }));
  }
}
